// Colour theme: 'system' (follows the OS, live), 'light' or 'dark'. Remembered per browser so it applies on the
// login page. The same logic runs once from the inline script in index.html before first paint (no flash).
import { useSyncExternalStore } from 'react';

export type ThemeChoice = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_KEY = 'qo.theme';
/** meta theme-color per resolved theme: the page background, so the mobile browser bar blends in. */
const BROWSER_BAR: Record<ResolvedTheme, string> = { light: '#f2f2f2', dark: '#1c1c1c' };

const dark = window.matchMedia('(prefers-color-scheme: dark)');
const isChoice = (v: string | null): v is ThemeChoice => v === 'system' || v === 'light' || v === 'dark';

const read = (): ThemeChoice => {
    try {
        const v = localStorage.getItem(THEME_KEY);
        return isChoice(v) ? v : 'system';
    } catch {
        return 'system';
    }
};

let choice: ThemeChoice = read();
const listeners = new Set<() => void>();

const resolve = (c: ThemeChoice): ResolvedTheme => (c === 'system' ? (dark.matches ? 'dark' : 'light') : c);

const apply = () => {
    const theme = resolve(choice);
    document.documentElement.dataset.theme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BROWSER_BAR[theme]);
    listeners.forEach((l) => l());
};

export const setTheme = (next: ThemeChoice): void => {
    choice = next;
    try {
        localStorage.setItem(THEME_KEY, next);
    } catch {
        // storage blocked: the choice lasts for this tab only
    }
    apply();
};

dark.addEventListener('change', () => {
    if (choice === 'system') apply();
});
// Another tab changed it.
window.addEventListener('storage', (e) => {
    if (e.key === THEME_KEY) {
        choice = read();
        apply();
    }
});
apply();

const subscribe = (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
};

export const useTheme = (): { choice: ThemeChoice; resolved: ResolvedTheme } => {
    const c = useSyncExternalStore(subscribe, () => choice);
    const resolved = useSyncExternalStore(subscribe, () => resolve(choice));
    return { choice: c, resolved };
};
